/**
 * Section marker: eyebrow, hairline, right-aligned mono meta, actions.
 *
 * This is what separates sections in the design, not a bordered card with a
 * header. A page of four bands reads as one document with labelled parts;
 * the same page built from four cards reads as four separate things that
 * happen to be stacked.
 */
import type { CSSProperties, ReactNode } from 'react';

import { Inline, Typography } from './finixt.js';

export interface BandProps {
  label: string;
  /** Right-aligned meta, rendered mono: a count, a timestamp. */
  meta?: ReactNode;
  actions?: ReactNode;
  /** Drop the hairline, for a label sitting directly on a card or rail. */
  bare?: boolean | undefined;
  style?: CSSProperties | undefined;
}

export function Band({ label, meta, actions, bare = false, style }: BandProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, ...style }}>
      <Typography variant="eyebrow" style={{ fontWeight: 'var(--weight-bold)' }}>
        {label}
      </Typography>
      {bare ? null : <span style={{ flex: 1, height: 1, background: 'var(--border-hairline)' }} />}
      {meta ? (
        <span
          className="mono"
          style={{ flexShrink: 0, fontSize: 'var(--text-label-lg)', color: 'var(--text-faint)' }}
        >
          {meta}
        </span>
      ) : null}
      {actions ? (
        <Inline wrap={false} align="center" style={{ flexShrink: 0 }}>
          {actions}
        </Inline>
      ) : null}
    </div>
  );
}
